import React from 'react';
import { IStyle } from 'fela';
import { Transition } from 'react-transition-group';
import {
  TransitionProps,
  TransitionStatus,
  UNMOUNTED,
} from 'react-transition-group/Transition';
import { theme as dmTheme, useFela } from '../../index';

type SlideProps = TransitionProps & { timeout: number };

const resolveElementStyles = ({
  state,
  timeout,
}: {
  state: TransitionStatus;
  timeout: number;
}): IStyle => {
  const defaultStyles = {
    flex: '1 1 auto',
    width: '100%',
    transition: `transform ease-in-out ${timeout}ms`,
  };

  const onScreenStyle = {
    transform: `translateX(0)`,
  };

  // `xxs` on mobile and `xs` on desktop is a little more than we need — but because of the drop shadow on the notification we use a little buffer.
  // TODO: Test `calc()` transitions in IE
  const offScreenStyle = {
    transform: `translateX(calc(100% + ${dmTheme.dimension.spacing.xxs.rem}))`,
    [dmTheme.breakpoints.horizontal.s.mediaQuery]: {
      transform: `translateX(calc(100% + ${dmTheme.dimension.spacing.xs.rem}))`,
    },
  };

  const transitions = {
    entering: {
      ...offScreenStyle,
    },
    entered: {
      ...onScreenStyle,
    },
    exiting: {
      ...offScreenStyle,
    },
    exited: {
      ...offScreenStyle,
    },
  };

  const styles = {
    ...defaultStyles,
    ...transitions[state as Exclude<TransitionStatus, typeof UNMOUNTED>],
  };

  return styles;
};

/**
 * The `Slide` component is used for animating the horizontal position of its children (sliding in and out).
 *
 * Most commonly it's used to animate the mounting and unmounting, but can also be used to describe in-place transition states as well.
 * It uses react-transition-group internally and can provide a rich set of event callbacks which we can use to handle enter and exit states.
 */
const Slide = ({
  children,
  in: inProp,
  onEnter,
  onEntered,
  onEntering,
  onExit,
  onExited,
  onExiting,
  timeout,
  ...otherProps
}: SlideProps) => {
  const { css } = useFela();

  const handleEnter = (node: HTMLElement, isAppearing: boolean) => {
    if (onEnter) {
      onEnter(node, isAppearing);
    }
  };

  const handleEntering = (node: HTMLElement, isAppearing: boolean) => {
    if (onEntering) {
      onEntering(node, isAppearing);
    }
  };

  const handleEntered = (node: HTMLElement, isAppearing: boolean) => {
    if (onEntered) {
      onEntered(node, isAppearing);
    }
  };

  const handleExit = (node: HTMLElement) => {
    if (onExit) {
      onExit(node);
    }
  };

  const handleExiting = (node: HTMLElement) => {
    if (onExiting) {
      onExiting(node);
    }
  };

  const handleExited = (node: HTMLElement) => {
    if (onExited) {
      onExited(node);
    }
  };

  return (
    <Transition
      in={inProp}
      timeout={timeout}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onEntered={handleEntered}
      onExit={handleExit}
      onExiting={handleExiting}
      onExited={handleExited}
      {...otherProps}>
      {state => {
        const elementStyles = resolveElementStyles({
          state,
          timeout,
        });
        return (
          <div data-dmid="slide" className={css(elementStyles)}>
            {children}
          </div>
        );
      }}
    </Transition>
  );
};

export default React.memo(Slide);
